import { useState, useEffect } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { freeOffersAPI, freeInvoicesAPI, customersAPI, signingRequestsAPI, getErrorMessage } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  ArrowLeft,
  Printer,
  FileText,
  Calendar,
  User,
  Euro,
  Loader2,
  Eye,
  EyeOff,
  Download,
  Pencil,
  Lock,
  Send,
  CheckCircle,
  FileSignature,
  RefreshCw,
  Clock,
  Mail
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import IncognitoBadge from "../components/IncognitoBadge";
import IncognitoToggleButton from "../components/IncognitoToggleButton";
import { Separator } from "../components/ui/separator";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";

export default function FreeOfferDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [offer, setOffer] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [showItemPrices, setShowItemPrices] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [sending, setSending] = useState(false);
  
  // Signing dialog state
  const [showSigningDialog, setShowSigningDialog] = useState(false);
  const [signingData, setSigningData] = useState({
    recipient_name: "",
    recipient_email: "",
    message: "",
    expires_in_days: 30
  });
  const [sendingForSignature, setSendingForSignature] = useState(false);
  
  // Existing signing request state
  const [existingSigningRequest, setExistingSigningRequest] = useState(null);
  const [loadingSigningRequest, setLoadingSigningRequest] = useState(false);
  const [resendingLink, setResendingLink] = useState(false);
  const [showResendDialog, setShowResendDialog] = useState(false);
  const [resendEmail, setResendEmail] = useState("");
  const [resendName, setResendName] = useState("");
  
  // Mark as accepted state
  const [markingAccepted, setMarkingAccepted] = useState(false);
  
  // Create invoice dialog state
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
  const [invoicePercentage, setInvoicePercentage] = useState(100);
  const [invoiceTitleSuffix, setInvoiceTitleSuffix] = useState("");
  const [creatingInvoice, setCreatingInvoice] = useState(false);

  useEffect(() => {
    loadOffer();
  }, [id]);

  // Auto-open invoice dialog when arriving with ?openInvoice=partial|final
  useEffect(() => {
    if (!offer) return;
    const openInvoice = searchParams.get("openInvoice");
    if (openInvoice) {
      const isFinal = ["final", "schluss", "schlussrechnung"].includes(openInvoice);
      if (isFinal) {
        setInvoicePercentage(100);
        setInvoiceTitleSuffix("Schlussrechnung");
      } else {
        setInvoiceTitleSuffix("1. Abschlagszahlung");
      }
      setShowInvoiceDialog(true);
      searchParams.delete("openInvoice");
      setSearchParams(searchParams, { replace: true });
    }
  }, [offer, searchParams, setSearchParams]);

  // Load existing signing request for this free offer
  useEffect(() => {
    const loadExistingSigningRequest = async () => {
      if (!id) return;
      setLoadingSigningRequest(true);
      try {
        // Fetch all signing requests and filter for this offer
        const response = await signingRequestsAPI.list({ limit: 100 });
        const requests = response.data.signing_requests || [];
        // Find active signing request for this free offer
        const existingReq = requests.find(req => 
          req.offer_id === id && 
          req.offer_type === "FREE_TEXT" &&
          !["expired", "revoked", "completed_digital", "completed_manual"].includes(req.status)
        );
        setExistingSigningRequest(existingReq || null);
      } catch (error) {
        console.warn("Could not check existing signing requests:", error);
      } finally {
        setLoadingSigningRequest(false);
      }
    };
    loadExistingSigningRequest();
  }, [id]);

  const loadOffer = async () => {
    try {
      const offerRes = await freeOffersAPI.get(id);
      setOffer(offerRes.data);
      
      // Load customer details
      if (offerRes.data.customer_id) {
        try {
          const customerRes = await customersAPI.get(offerRes.data.customer_id);
          setCustomer(customerRes.data);
        } catch (e) {
          console.warn("Could not load customer details");
        }
      }
    } catch (error) {
      console.error("Failed to load offer:", error);
      toast.error("Fehler beim Laden des Angebots");
      navigate("/angebote");
    } finally {
      setLoading(false);
    }
  };

  const handleFinalize = async () => {
    setFinalizing(true);
    try {
      await freeOffersAPI.finalize(id);
      toast.success("Angebot wurde finalisiert");
      loadOffer();
    } catch (error) {
      console.error("Failed to finalize offer:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Finalisieren");
    } finally {
      setFinalizing(false);
    }
  };

  const handleSendEmail = async () => {
    setSending(true);
    try {
      const response = await freeOffersAPI.sendEmail(id);
      toast.success(response.data.message || "Angebot wurde versendet");
      loadOffer();
    } catch (error) {
      console.error("Failed to send offer:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Versenden");
    } finally {
      setSending(false);
    }
  };

  // Open signing dialog with pre-filled customer data
  const openSigningDialog = () => {
    if (customer) {
      const fullName = [customer.first_name, customer.last_name].filter(Boolean).join(" ");
      setSigningData({
        recipient_name: fullName || customer.company_name || "",
        recipient_email: customer.email || "",
        message: `Bitte unterschreiben Sie unser Angebot ${offer?.offer_number}`,
        expires_in_days: 30
      });
    }
    setShowSigningDialog(true);
  };

  const handleSendForSignature = async () => {
    // Validate
    if (!signingData.recipient_name || !signingData.recipient_email) {
      toast.error("Bitte füllen Sie Name und E-Mail-Adresse aus");
      return;
    }
    
    setSendingForSignature(true);
    try {
      const response = await freeOffersAPI.sendForSignature(id, signingData);
      toast.success("Signaturanfrage wurde erstellt! E-Mail wird gesendet...");
      setShowSigningDialog(false);
      
      // Navigate to signing management or refresh
      navigate(`/signaturen`);
    } catch (error) {
      console.error("Failed to send for signature:", error);
      const detail = error.response?.data?.detail;
      if (typeof detail === "object") {
        toast.error(detail.message || "Fehler beim Erstellen der Signaturanfrage");
      } else {
        toast.error(detail || "Fehler beim Erstellen der Signaturanfrage");
      }
    } finally {
      setSendingForSignature(false);
    }
  };

  // Open resend dialog with pre-filled data
  const openResendDialog = () => {
    if (existingSigningRequest) {
      setResendEmail(existingSigningRequest.recipient_email || "");
      setResendName(existingSigningRequest.recipient_name || "");
    }
    setShowResendDialog(true);
  };

  // Resend signing link with optional email correction
  const handleResendLink = async () => {
    if (!existingSigningRequest) return;
    
    setResendingLink(true);
    try {
      const body = {};
      if (resendEmail && resendEmail !== existingSigningRequest.recipient_email) {
        body.recipient_email = resendEmail;
      }
      if (resendName && resendName !== existingSigningRequest.recipient_name) {
        body.recipient_name = resendName;
      }
      
      await signingRequestsAPI.resend(existingSigningRequest.id, body);
      toast.success(`Signatur-Link wurde an ${resendEmail || existingSigningRequest.recipient_email} gesendet!`);
      setShowResendDialog(false);
      
      // Refresh signing request data
      const response = await signingRequestsAPI.list({ limit: 100 });
      const requests = response.data.signing_requests || [];
      const updatedReq = requests.find(req => req.id === existingSigningRequest.id);
      if (updatedReq) {
        setExistingSigningRequest(updatedReq);
      }
    } catch (error) {
      console.error("Failed to resend signing link:", error);
      toast.error(error.response?.data?.detail || "Fehler beim erneuten Senden");
    } finally {
      setResendingLink(false);
    }
  };

  const handlePrint = async () => {
    setGenerating(true);
    try {
      // Generate PDF with options
      const pdfUrl = freeOffersAPI.getPdfUrl(id, showItemPrices);
      window.open(pdfUrl, "_blank");
    } catch (error) {
      console.error("Failed to generate PDF:", error);
      toast.error("Fehler beim Erstellen des PDFs");
    } finally {
      setGenerating(false);
    }
  };

  // Mark offer as accepted manually
  const handleMarkAccepted = async () => {
    setMarkingAccepted(true);
    try {
      await freeOffersAPI.markAccepted(id);
      toast.success("Angebot wurde als angenommen markiert");
      loadOffer();
    } catch (error) {
      console.error("Failed to mark as accepted:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Markieren");
    } finally {
      setMarkingAccepted(false);
    }
  };

  // Create invoice from offer
  const handleCreateInvoice = async () => {
    setCreatingInvoice(true);
    try {
      const response = await freeInvoicesAPI.createFromOffer({
        free_offer_id: id,
        percentage: invoicePercentage,
        title_suffix: invoiceTitleSuffix || null,
        payment_days: 14
      });
      toast.success(`Rechnung ${response.data.invoice_number} wurde erstellt`);
      setShowInvoiceDialog(false);
      // Navigate to the new invoice
      navigate(`/freitext-rechnungen/${response.data.id}`);
    } catch (error) {
      console.error("Failed to create invoice:", error?.response?.status, error?.response?.data);
      const msg = getErrorMessage(error, "Fehler beim Erstellen der Rechnung");
      toast.error("Fehler beim Erstellen der Rechnung", { description: msg, duration: 10000 });
    } finally {
      setCreatingInvoice(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: "EUR"
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric"
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!offer) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Angebot nicht gefunden</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate("/angebote")}>
          Zurück zur Übersicht
        </Button>
      </div>
    );
  }

  // Helper to get status badge
  const getStatusBadge = (status) => {
    const statusConfig = {
      erstellt: { variant: "secondary", label: "Entwurf", className: "bg-gray-100 text-gray-700" },
      finalisiert: { variant: "secondary", label: "Finalisiert", className: "bg-blue-100 text-blue-700" },
      versendet: { variant: "secondary", label: "Versendet", className: "bg-green-100 text-green-700" },
      angenommen: { variant: "secondary", label: "Angenommen", className: "bg-emerald-100 text-emerald-700" },
      abgelehnt: { variant: "secondary", label: "Abgelehnt", className: "bg-red-100 text-red-700" }
    };
    const config = statusConfig[status] || statusConfig.erstellt;
    return <Badge variant={config.variant} className={config.className}>{config.label}</Badge>;
  };

  const isEditable = offer?.status === "erstellt";
  const canFinalize = offer?.status === "erstellt";
  const canSend = offer?.status === "finalisiert" || offer?.status === "erstellt";

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold font-['Manrope']">
                {offer.offer_number}
              </h1>
              <Badge variant="secondary" className="bg-amber-100 text-amber-800">
                Freitext
              </Badge>
              {getStatusBadge(offer.status)}
              <IncognitoBadge active={offer.is_incognito} size="md" />
            </div>
            <p className="text-muted-foreground">{offer.title}</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2 flex-wrap">
          <IncognitoToggleButton user={user} docType="free_offer" docId={offer.id} active={offer.is_incognito} onToggled={loadOffer} />
          {/* Edit Button - only if not finalized */}
          {isEditable && (
            <Button variant="outline" asChild data-testid="edit-offer-btn">
              <Link to={`/freitext-angebote/${id}/bearbeiten`}>
                <Pencil className="h-4 w-4 mr-2" />
                Bearbeiten
              </Link>
            </Button>
          )}
          
          {/* Finalize Button */}
          {canFinalize && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" disabled={finalizing} data-testid="finalize-offer-btn">
                  {finalizing ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Lock className="h-4 w-4 mr-2" />
                  )}
                  Finalisieren
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Angebot finalisieren?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Nach dem Finalisieren kann das Angebot nicht mehr bearbeitet werden. 
                    Möchten Sie fortfahren?
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                  <AlertDialogAction onClick={handleFinalize}>
                    Finalisieren
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
          
          {/* Send Email Button */}
          {canSend && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" disabled={sending} data-testid="send-offer-btn">
                  {sending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4 mr-2" />
                  )}
                  Per E-Mail senden
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Angebot versenden?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Das Angebot wird an die E-Mail-Adresse des Kunden gesendet.
                    {customer?.email && (
                      <span className="block mt-2 font-medium">
                        Empfänger: {customer.email}
                      </span>
                    )}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                  <AlertDialogAction onClick={handleSendEmail}>
                    Senden
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
          
          {/* Send for Signature Button - only if NO active signing request exists */}
          {canSend && customer?.email && !existingSigningRequest && !loadingSigningRequest && (
            <Button 
              variant="default" 
              onClick={openSigningDialog}
              className="bg-green-600 hover:bg-green-700"
              data-testid="send-for-signature-btn"
            >
              <FileSignature className="h-4 w-4 mr-2" />
              Zur Unterschrift senden
            </Button>
          )}
          
          {/* Resend Signing Link Button - if active signing request exists */}
          {existingSigningRequest && (
            <Button 
              variant="default" 
              onClick={openResendDialog}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="resend-signing-link-btn"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Link erneut senden
            </Button>
          )}
          
          {/* Mark as Accepted Button - for offers that aren't accepted yet */}
          {(offer?.status === "finalisiert" || offer?.status === "erstellt" || offer?.status === "versendet") && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button 
                  variant="outline" 
                  disabled={markingAccepted}
                  className="border-emerald-500 text-emerald-600 hover:bg-emerald-50"
                  data-testid="mark-accepted-btn"
                >
                  {markingAccepted ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle className="h-4 w-4 mr-2" />
                  )}
                  Als angenommen markieren
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Angebot als angenommen markieren?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Das Angebot wird manuell als angenommen markiert. 
                    Nutzen Sie diese Funktion nur, wenn die Unterschrift offline erfolgt ist 
                    oder der digitale Signaturprozess fehlgeschlagen ist.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                  <AlertDialogAction onClick={handleMarkAccepted} className="bg-emerald-600 hover:bg-emerald-700">
                    Als angenommen markieren
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
          
          {/* Create Invoice Button - only for accepted offers */}
          {offer?.status === "angenommen" && (
            <Button 
              variant="default" 
              onClick={() => setShowInvoiceDialog(true)}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="create-invoice-btn"
            >
              <Euro className="h-4 w-4 mr-2" />
              Rechnung erstellen
            </Button>
          )}
          
          {/* Print Options */}
          <div className="flex items-center gap-2 px-3 py-2 border rounded-lg bg-muted/50">
            <Switch
              id="show-prices"
              checked={showItemPrices}
              onCheckedChange={setShowItemPrices}
            />
            <Label htmlFor="show-prices" className="text-sm cursor-pointer">
              {showItemPrices ? (
                <span className="flex items-center gap-1">
                  <Eye className="h-4 w-4" /> Einzelpreise
                </span>
              ) : (
                <span className="flex items-center gap-1">
                  <EyeOff className="h-4 w-4" /> Nur Endsumme
                </span>
              )}
            </Label>
          </div>
          
          <Button onClick={handlePrint} disabled={generating} variant="outline">
            {generating ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Wird erstellt...
              </>
            ) : (
              <>
                <Printer className="h-4 w-4 mr-2" />
                PDF
              </>
            )}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Active Signing Request Info */}
          {existingSigningRequest && (
            <Card className="border-blue-200 bg-blue-50/50">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-blue-100 rounded-full">
                      <FileSignature className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="font-medium text-blue-900">Aktive Signaturanfrage</h4>
                      <p className="text-sm text-blue-700 mt-1">
                        Gesendet an: <span className="font-medium">{existingSigningRequest.recipient_email}</span>
                      </p>
                      <p className="text-xs text-blue-600 mt-1">
                        Status: {existingSigningRequest.status === "sent" ? "Gesendet" : 
                                existingSigningRequest.status === "opened" ? "Geöffnet" :
                                existingSigningRequest.status === "otp_sent" ? "OTP gesendet" :
                                existingSigningRequest.status === "otp_verified" ? "OTP verifiziert" :
                                existingSigningRequest.status}
                      </p>
                    </div>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={openResendDialog}
                    className="border-blue-300 text-blue-700 hover:bg-blue-100"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    E-Mail korrigieren
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Customer Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <User className="h-5 w-5" />
                Kunde
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <p className="font-medium text-lg">{offer.customer_name}</p>
                <p className="text-muted-foreground whitespace-pre-line">
                  {offer.customer_address}
                </p>
                {customer && (
                  <div className="mt-4 pt-4 border-t">
                    {customer.email && (
                      <p className="text-sm">
                        <span className="text-muted-foreground">E-Mail: </span>
                        {customer.email}
                      </p>
                    )}
                    {customer.phone && (
                      <p className="text-sm">
                        <span className="text-muted-foreground">Telefon: </span>
                        {customer.phone}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Line Items */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Positionen
              </CardTitle>
              <CardDescription>
                {offer.items?.length || 0} Position(en)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">#</TableHead>
                    <TableHead>Beschreibung</TableHead>
                    <TableHead className="text-center w-20">Menge</TableHead>
                    <TableHead className="text-center w-24">Einheit</TableHead>
                    {showItemPrices && (
                      <>
                        <TableHead className="text-right w-28">Einzelpreis</TableHead>
                        <TableHead className="text-right w-28">Gesamt</TableHead>
                      </>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {offer.items?.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell className="font-medium text-muted-foreground">
                        {index + 1}
                      </TableCell>
                      <TableCell>
                        <p className="whitespace-pre-wrap">{item.description}</p>
                      </TableCell>
                      <TableCell className="text-center">{item.quantity}</TableCell>
                      <TableCell className="text-center">{item.unit}</TableCell>
                      {showItemPrices && (
                        <>
                          <TableCell className="text-right font-mono">
                            {formatCurrency(item.unit_price)}
                          </TableCell>
                          <TableCell className="text-right font-mono font-medium">
                            {formatCurrency(item.total)}
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Notes */}
          {offer.notes && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Anmerkungen</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="whitespace-pre-wrap text-muted-foreground">
                  {offer.notes}
                </p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Price Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Euro className="h-5 w-5" />
                Preisübersicht
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Nettobetrag</span>
                <span className="font-mono">{formatCurrency(offer.subtotal)}</span>
              </div>
              
              {offer.include_vat && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">MwSt. ({offer.vat_rate}%)</span>
                  <span className="font-mono">{formatCurrency(offer.vat_amount)}</span>
                </div>
              )}
              
              <Separator />
              
              <div className="flex justify-between text-lg font-bold">
                <span>Gesamtbetrag</span>
                <span className="text-primary font-mono">{formatCurrency(offer.total)}</span>
              </div>
              
              {!offer.include_vat && (
                <p className="text-xs text-muted-foreground text-center">
                  (ohne Mehrwertsteuer)
                </p>
              )}
            </CardContent>
          </Card>

          {/* Offer Details */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Erstellt am</span>
                <span>{formatDate(offer.created_at)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Zahlungsziel</span>
                <span>{offer.payment_days} Tage</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">MwSt.</span>
                <span>{offer.include_vat ? `${offer.vat_rate}%` : "Ohne"}</span>
              </div>
              {offer.pdf_generated && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">PDF erstellt</span>
                  <Badge variant="outline" className="text-green-600">Ja</Badge>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Aktionen</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button 
                variant="outline" 
                className="w-full justify-start"
                onClick={handlePrint}
                disabled={generating}
              >
                <Download className="h-4 w-4 mr-2" />
                PDF herunterladen
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start"
                asChild
              >
                <Link to="/angebote">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Zurück zur Übersicht
                </Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
      
      {/* Signing Dialog */}
      <Dialog open={showSigningDialog} onOpenChange={setShowSigningDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSignature className="h-5 w-5 text-green-600" />
              Zur Unterschrift senden
            </DialogTitle>
            <DialogDescription>
              Senden Sie dieses Freitext-Angebot zur digitalen Unterschrift.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="recipient_name">Empfängername *</Label>
              <Input
                id="recipient_name"
                value={signingData.recipient_name}
                onChange={(e) => setSigningData(prev => ({ ...prev, recipient_name: e.target.value }))}
                placeholder="Max Mustermann"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="recipient_email">E-Mail-Adresse *</Label>
              <Input
                id="recipient_email"
                type="email"
                value={signingData.recipient_email}
                onChange={(e) => setSigningData(prev => ({ ...prev, recipient_email: e.target.value }))}
                placeholder="kunde@beispiel.de"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="message">Nachricht (optional)</Label>
              <Textarea
                id="message"
                value={signingData.message}
                onChange={(e) => setSigningData(prev => ({ ...prev, message: e.target.value }))}
                placeholder="Persönliche Nachricht an den Empfänger..."
                rows={3}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="expires_in_days">Gültigkeitsdauer (Tage)</Label>
              <Input
                id="expires_in_days"
                type="number"
                min="1"
                max="90"
                value={signingData.expires_in_days}
                onChange={(e) => setSigningData(prev => ({ ...prev, expires_in_days: parseInt(e.target.value) || 30 }))}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSigningDialog(false)}>
              Abbrechen
            </Button>
            <Button 
              onClick={handleSendForSignature} 
              disabled={sendingForSignature}
              className="bg-green-600 hover:bg-green-700"
            >
              {sendingForSignature ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Wird gesendet...
                </>
              ) : (
                <>
                  <FileSignature className="h-4 w-4 mr-2" />
                  Signaturanfrage erstellen
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Resend Signing Link Dialog */}
      <Dialog open={showResendDialog} onOpenChange={setShowResendDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="h-5 w-5 text-blue-600" />
              Link erneut senden
            </DialogTitle>
            <DialogDescription>
              E-Mail-Adresse prüfen oder korrigieren und Link erneut senden.
            </DialogDescription>
          </DialogHeader>
          
          {existingSigningRequest && (
            <div className="space-y-4 py-4">
              {/* Current Status Info */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
                <div className="flex items-center gap-2 text-blue-700 font-medium mb-1">
                  <Mail className="h-4 w-4" />
                  Aktive Signaturanfrage
                </div>
                <div className="text-blue-600">
                  Status: {existingSigningRequest.status === "sent" ? "Gesendet" : 
                          existingSigningRequest.status === "opened" ? "Geöffnet" :
                          existingSigningRequest.status === "otp_sent" ? "OTP gesendet" :
                          existingSigningRequest.status}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="resend-name">Empfänger Name</Label>
                <Input
                  id="resend-name"
                  value={resendName}
                  onChange={(e) => setResendName(e.target.value)}
                  placeholder="Name des Empfängers"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="resend-email">E-Mail-Adresse</Label>
                <Input
                  id="resend-email"
                  type="email"
                  value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)}
                  placeholder="E-Mail-Adresse"
                />
                {resendEmail && resendEmail !== existingSigningRequest.recipient_email && (
                  <p className="text-xs text-amber-600">
                    E-Mail wird von "{existingSigningRequest.recipient_email}" auf "{resendEmail}" geändert
                  </p>
                )}
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResendDialog(false)} disabled={resendingLink}>
              Abbrechen
            </Button>
            <Button 
              onClick={handleResendLink} 
              disabled={resendingLink || !resendEmail}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {resendingLink ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Wird gesendet...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Link senden
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Invoice Dialog */}
      <Dialog open={showInvoiceDialog} onOpenChange={setShowInvoiceDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {invoiceTitleSuffix === "Schlussrechnung" ? "Schlussrechnung erstellen" : "Rechnung aus Angebot erstellen"}
            </DialogTitle>
            <DialogDescription>
              {invoiceTitleSuffix === "Schlussrechnung"
                ? "Schlussrechnung über den vollen (oder reduzierten) Restbetrag des Angebots."
                : "Erstellen Sie eine Rechnung basierend auf diesem Angebot. Sie können einen Prozentsatz für Teilzahlungen angeben."}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Quick percentage buttons */}
            <div className="space-y-2">
              <Label>Rechnungsbetrag</Label>
              <div className="flex flex-wrap gap-2">
                {[30, 60, 100].map((pct) => (
                  <Button
                    key={pct}
                    type="button"
                    variant={invoicePercentage === pct ? "default" : "outline"}
                    size="sm"
                    onClick={() => setInvoicePercentage(pct)}
                  >
                    {pct}%
                  </Button>
                ))}
              </div>
              <div className="flex items-center gap-2 mt-2">
                <Input
                  type="number"
                  min="1"
                  max="100"
                  value={invoicePercentage}
                  onChange={(e) => setInvoicePercentage(Number(e.target.value))}
                  className="w-24"
                />
                <span className="text-muted-foreground">%</span>
                <span className="text-sm text-muted-foreground ml-2">
                  = {formatCurrency((offer?.total || 0) * invoicePercentage / 100)}
                </span>
              </div>
            </div>
            
            {/* Title suffix */}
            <div className="space-y-2">
              <Label htmlFor="title-suffix">Bezeichnung (optional)</Label>
              <Input
                id="title-suffix"
                placeholder="z.B. 1. Abschlagszahlung"
                value={invoiceTitleSuffix}
                onChange={(e) => setInvoiceTitleSuffix(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Wird dem Rechnungstitel hinzugefügt
              </p>
            </div>
            
            {/* Preview */}
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-sm font-medium">Vorschau:</p>
              <p className="text-sm text-muted-foreground">
                Titel: {offer?.title} {invoiceTitleSuffix ? `- ${invoiceTitleSuffix}` : invoicePercentage !== 100 ? `(${invoicePercentage}%)` : ""}
              </p>
              <p className="text-sm font-medium mt-1">
                Betrag: {formatCurrency((offer?.total || 0) * invoicePercentage / 100)}
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInvoiceDialog(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleCreateInvoice} disabled={creatingInvoice}>
              {creatingInvoice ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Erstellen...
                </>
              ) : (
                <>
                  <Euro className="h-4 w-4 mr-2" />
                  Rechnung erstellen
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
